/**
 * AGROMX Frontend - Persona Service
 * Servicio para gestión del catálogo de personas (clientes, proveedores, responsables)
 */

import api from './api.js';

class PersonaService {
  /**
   * Listar personas con filtros y paginación
   */
  async list(params = {}) {
    const response = await api.get('/personas', params);
    return response;
  }

  /**
   * Listar personas por rol específico (para selects)
   */
  async listByRol(rol) {
    const response = await api.get(`/personas/rol/${rol}`);
    return response.data || response;
  }

  /**
   * Listar personas por rol con filtros de área y puesto
   */
  async listByRolFiltrado(rol, area, puesto) {
    const params = {};
    if (area) params.area = area;
    if (puesto) params.puesto = puesto;
    const response = await api.get(`/personas/rol/${rol}/filtrar`, params);
    return response.data || response;
  }

  /**
   * Obtener persona por ID con todos sus detalles
   */
  async getById(id) {
    const response = await api.get(`/personas/${id}`);
    return response.data || response;
  }

  /**
   * Crear nueva persona
   */
  async create(data) {
    const response = await api.post('/personas', data);
    return response.data || response;
  }

  /**
   * Actualizar persona
   */
  async update(id, data) {
    const response = await api.put(`/personas/${id}`, data);
    return response.data || response;
  }

  /**
   * Eliminar persona (soft delete)
   */
  async delete(id) {
    await api.delete(`/personas/${id}`);
    return true;
  }

  /**
   * Desactivar un rol específico de una persona (sin eliminar la persona).
   */
  async desactivarRolPorPersona(personaId, rol) {
    await api.delete(`/personas/${personaId}/rol/${rol}`);
    return true;
  }

  // ============================================
  // GESTIÓN DE ROLES
  // ============================================

  /**
   * Agregar rol a persona
   */
  async agregarRol(personaId, data) {
    const response = await api.post(`/personas/${personaId}/roles`, data);
    return response.data || response;
  }

  /**
   * Eliminar rol de persona
   */
  async eliminarRol(personaId, rolId) {
    await api.delete(`/personas/${personaId}/roles/${rolId}`);
    return true;
  }

  /**
   * Desactivar rol de persona
   */
  async desactivarRol(personaId, rolId) {
    const response = await api.patch(`/personas/${personaId}/roles/${rolId}/desactivar`);
    return response.data || response;
  }

  // ============================================
  // GESTIÓN DE CONTACTOS
  // ============================================

  /**
   * Agregar contacto a persona
   */
  async agregarContacto(personaId, data) {
    const response = await api.post(`/personas/${personaId}/contactos`, data);
    return response.data || response;
  }

  /**
   * Eliminar contacto de persona
   */
  async eliminarContacto(personaId, contactoId) {
    await api.delete(`/personas/${personaId}/contactos/${contactoId}`);
    return true;
  }

  // ============================================
  // GESTIÓN DE CUENTAS BANCARIAS
  // ============================================

  /**
   * Agregar cuenta bancaria a persona
   */
  async agregarCuentaBancaria(personaId, data) {
    const response = await api.post(`/personas/${personaId}/cuentas`, data);
    return response.data || response;
  }

  /**
   * Eliminar cuenta bancaria de persona
   */
  async eliminarCuentaBancaria(personaId, cuentaId) {
    await api.delete(`/personas/${personaId}/cuentas/${cuentaId}`);
    return true;
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Obtener lista de roles disponibles
   */
  getRoles() {
    return [
      { value: 'cliente', label: 'Cliente' },
      { value: 'proveedor', label: 'Proveedor' },
      { value: 'distribuidor', label: 'Distribuidor' },
      { value: 'responsable', label: 'Responsable/Empleado' },
      { value: 'agente_aduanal', label: 'Agente Aduanal' }
    ];
  }

  /**
   * Obtener lista de tipos de persona
   */
  getTiposPersona() {
    return [
      { value: 'fisica', label: 'Persona Física' },
      { value: 'moral', label: 'Persona Moral (Empresa)' }
    ];
  }

  /**
   * Obtener lista de estados de México
   */
  getEstadosMexico() {
    return [
      'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
      'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima',
      'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo',
      'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
      'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
      'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
    ];
  }

  /**
   * Obtener áreas para responsables
   */
  getAreasResponsable() {
    return [
      { value: 'almacen', label: 'Almacén' },
      { value: 'produccion', label: 'Producción' },
      { value: 'calidad', label: 'Calidad' },
      { value: 'ventas', label: 'Ventas' },
      { value: 'posventa', label: 'Posventa' },
      { value: 'compras', label: 'Compras' },
      { value: 'administracion', label: 'Administración' },
      { value: 'direccion', label: 'Dirección' }
    ];
  }

  /**
   * Formatear nombre de rol para mostrar
   */
  formatRol(rol) {
    const roles = {
      cliente: 'Cliente',
      proveedor: 'Proveedor',
      distribuidor: 'Distribuidor',
      responsable: 'Responsable',
      agente_aduanal: 'Agente Aduanal'
    };
    return roles[rol] || rol;
  }

  /**
   * Obtener color del badge según el rol
   */
  getRolBadgeClass(rol) {
    const classes = {
      cliente: 'badge-success',
      proveedor: 'badge-primary',
      distribuidor: 'badge-info',
      responsable: 'badge-warning',
      agente_aduanal: 'badge-secondary'
    };
    return classes[rol] || 'badge-secondary';
  }

  /**
   * Formatear tipo de persona
   */
  formatTipoPersona(tipo) {
    return tipo === 'fisica' ? 'Persona Física' : 'Persona Moral';
  }

  /**
   * Formatear fecha para mostrar
   */
  formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}

export default new PersonaService();
